import { useEffect, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  CancelSubmitButtons,
  Table,
  TableHead,
  TableHeader,
} from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import {
  useGetCurrentOrganizationId,
  useUpdateSupplementalQuestionsOrder,
} from "~/providers/hooks";
import { SortableSupplementalQuestion } from "./SortableSupplementalQuestion";
import { useSupplementalQuestionsEditorContext } from "./SupplementalQuestionsEditor";

export const ReorderSupplementalQuestionsModal = (props: ModalProps) => {
  const { questions, type: consultationType } =
    useSupplementalQuestionsEditorContext();
  const organizationId = useGetCurrentOrganizationId();
  const updateSupplementalQuestionsOrderMutation =
    useUpdateSupplementalQuestionsOrder();

  const [questionsCopy, setQuestionsCopy] = useState([...questions]);

  useEffect(() => {
    if (props.show) {
      setQuestionsCopy([...questions]);
    }
  }, [props.show, questions]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setQuestionsCopy((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = () => {
    const questionIds = questionsCopy.map((question) => question.id);

    if (!organizationId) return;

    updateSupplementalQuestionsOrderMutation.mutate(
      {
        consultationType,
        organizationId,
        questionIds,
      },
      {
        onSuccess: () => props.onClose(),
      },
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={questionsCopy}
        strategy={verticalListSortingStrategy}
      >
        <Modal title="Edit order" className="max-h-[80vh] xl:w-1/2" {...props}>
          <div className="w-full overflow-x-auto rounded-xl border border-gray-200">
            <Table className="size-full table-auto">
              <TableHead className="rounded-t-xl">
                <tr>
                  <TableHeader />
                  <TableHeader>Question</TableHeader>
                  <TableHeader>Updated</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {questionsCopy.map((question, index) => (
                  <SortableSupplementalQuestion
                    key={question.id}
                    question={question}
                    order={index + 1}
                  />
                ))}
              </tbody>
            </Table>
          </div>

          <CancelSubmitButtons
            submitButtonLabel="Save changes"
            handleCancel={() => props.onClose()}
            isSubmitting={updateSupplementalQuestionsOrderMutation.isPending}
            containerClassName="w-fit ml-auto"
            disableSubmit={false}
            disableCancel={false}
            handleSubmit={handleSubmit}
          />
        </Modal>
      </SortableContext>
    </DndContext>
  );
};
